/**
 * Scroll Reveal Composable
 *
 * A smooth, performant scroll-based reveal animation system
 * inspired by Apple.com and IKEA.com patterns.
 *
 * Features:
 * - IntersectionObserver for efficient scroll detection
 * - Alpine.js directive integration (x-reveal)
 * - Multiple animation types (fade-up, fade-scale, fade-blur, clip-reveal)
 * - Staggered animations for grid items
 * - Respects prefers-reduced-motion
 * - GPU-accelerated transforms
 *
 * Usage in Twig/HTML:
 *
 * Basic reveal (fade + rise):
 *   <div x-reveal>...</div>
 *
 * With animation type:
 *   <div x-reveal="'fade-scale'">...</div>
 *   <div x-reveal="'fade-blur'">...</div>
 *   <div x-reveal="'clip-left'">...</div>
 *
 * Staggered grid (auto-calculates delay per child):
 *   <div x-reveal-stagger>
 *     <div x-reveal>Item 1</div>
 *     <div x-reveal>Item 2</div>
 *     <div x-reveal>Item 3</div>
 *   </div>
 *
 * Custom delay (in ms):
 *   <div x-reveal.delay-200>...</div>
 *
 * Data attribute approach (no Alpine):
 *   <div data-reveal="fade-up">...</div>
 *   <div data-reveal="fade-scale" data-reveal-delay="200">...</div>
 */

// Types
type RevealType = 'fade-up' | 'fade-scale' | 'fade-blur' | 'clip-left' | 'clip-right' | 'clip-up' | 'clip-down';

interface RevealOptions {
    type: RevealType;
    delay: number;
    threshold: number;
    rootMargin: string;
}

// Default configuration
const DEFAULT_OPTIONS: RevealOptions = {
    type: 'fade-up',
    delay: 0,
    threshold: 0.15, // Trigger when 15% visible
    rootMargin: '0px 0px -50px 0px', // Start slightly before element enters viewport
};

// Stagger configuration
const STAGGER_DELAY_INCREMENT = 100; // ms between each item

// Check for reduced motion preference
const prefersReducedMotion = (): boolean => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Create the IntersectionObserver
let observer: IntersectionObserver | null = null;
const observedElements = new WeakMap<Element, RevealOptions>();

const getObserver = (): IntersectionObserver => {
    if (!observer) {
        observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const el = entry.target as HTMLElement;
                        const options = observedElements.get(el) || DEFAULT_OPTIONS;

                        // Apply the reveal with configured delay
                        setTimeout(() => {
                            el.classList.add('revealed');
                            el.classList.remove('reveal-hidden');
                        }, options.delay);

                        // Stop observing once revealed (one-time animation)
                        observer?.unobserve(el);
                        observedElements.delete(el);
                    }
                });
            },
            {
                threshold: DEFAULT_OPTIONS.threshold,
                rootMargin: DEFAULT_OPTIONS.rootMargin,
            }
        );
    }
    return observer;
};

// Setup reveal for a single element
const setupReveal = (el: HTMLElement, options: Partial<RevealOptions> = {}): void => {
    // Skip if reduced motion is preferred
    if (prefersReducedMotion()) {
        el.classList.add('revealed');
        return;
    }

    const mergedOptions: RevealOptions = { ...DEFAULT_OPTIONS, ...options };

    // Add the base reveal class based on type
    el.classList.add('reveal', `reveal-${mergedOptions.type}`, 'reveal-hidden');

    // Store options and observe
    observedElements.set(el, mergedOptions);
    getObserver().observe(el);
};

// Initialize all data-reveal elements
const initDataRevealElements = (): void => {
    document.querySelectorAll<HTMLElement>('[data-reveal]').forEach((el) => {
        // Skip if already initialized
        if (el.classList.contains('reveal')) return;

        const type = (el.dataset.reveal || 'fade-up') as RevealType;
        const delay = parseInt(el.dataset.revealDelay || '0', 10);

        setupReveal(el, { type, delay });
    });

    // Handle stagger containers
    document.querySelectorAll<HTMLElement>('[data-reveal-stagger]').forEach((container) => {
        const children = container.querySelectorAll<HTMLElement>('[data-reveal]');
        children.forEach((child, index) => {
            const baseDelay = parseInt(child.dataset.revealDelay || '0', 10);
            child.dataset.revealDelay = String(baseDelay + index * STAGGER_DELAY_INCREMENT);
        });
    });
};

// Alpine.js directive: x-reveal
const createAlpineDirective = (Alpine: typeof window.Alpine): void => {
    Alpine.directive('reveal', (el, { expression, modifiers }, { evaluate }) => {
        // Parse animation type from expression
        let type: RevealType = 'fade-up';
        if (expression) {
            const evaluated = evaluate(expression);
            if (typeof evaluated === 'string') {
                type = evaluated as RevealType;
            }
        }

        // Parse delay from modifiers (e.g., x-reveal.delay-200)
        let delay = 0;
        modifiers.forEach((mod) => {
            if (mod.startsWith('delay-')) {
                delay = parseInt(mod.replace('delay-', ''), 10);
            }
        });

        setupReveal(el as HTMLElement, { type, delay });
    });

    // Alpine.js directive: x-reveal-stagger (for parent containers)
    Alpine.directive('reveal-stagger', (el, { expression }, { evaluate }) => {
        // Get increment from expression or use default
        let increment = STAGGER_DELAY_INCREMENT;
        if (expression) {
            const evaluated = evaluate(expression);
            if (typeof evaluated === 'number') {
                increment = evaluated;
            }
        }

        // Use MutationObserver to handle dynamically added children
        const applyStagger = () => {
            const children = el.querySelectorAll('[x-reveal], .reveal') as NodeListOf<HTMLElement>;
            children.forEach((child, index) => {
                const currentDelay = observedElements.get(child)?.delay || 0;
                const newDelay = currentDelay + index * increment;

                // Update the stored options
                const options = observedElements.get(child);
                if (options) {
                    options.delay = newDelay;
                    observedElements.set(child, options);
                }
            });
        };

        // Apply stagger after a microtask to let children initialize
        queueMicrotask(applyStagger);
    });
};

// Main initialization function
export const initScrollReveal = (): void => {
    // Initialize data-attribute based reveals
    initDataRevealElements();

    // Setup Alpine directive if Alpine is available
    if (window.Alpine) {
        createAlpineDirective(window.Alpine);
    }
};

// Export for use in Alpine.plugin()
export const scrollRevealPlugin = (Alpine: typeof window.Alpine): void => {
    createAlpineDirective(Alpine);
};

// Cleanup function (useful for SPA/HMR)
export const destroyScrollReveal = (): void => {
    observer?.disconnect();
    observer = null;
};

// Auto-init on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDataRevealElements);
} else {
    initDataRevealElements();
}

// Support HMR
if (import.meta.hot) {
    import.meta.hot.accept(() => {
        destroyScrollReveal();
        initDataRevealElements();
    });
}
